import client from "./client";
import type {
  EmployeeEnrollment,
  EnrollmentIssuesResponse,
  EnrollmentPhotoDeleteResponse,
  EnrollmentPhotoMetadata,
  EnrollmentPhotoReplaceResponse,
  EnrollmentRetryConflict,
  EnrollmentRetryResponse,
} from "../interfaces/SelfieEnrollment";

export function getEnrollmentPhotoErrorMessage(err: unknown): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { message?: string }; status?: number } }).response;
    if (response?.data && typeof response.data === "object" && "message" in response.data) {
      const msg = (response.data as { message?: string }).message;
      if (msg) return msg;
    }
  }
  if (err instanceof Error) return err.message;
  return "An error occurred.";
}

export async function getEnrollmentPhotoMetadata(employeeId: number): Promise<EnrollmentPhotoMetadata> {
  const response = await client.get<EnrollmentPhotoMetadata>(
    `admin/employees/${employeeId}/enrollmentPhoto`
  );
  return response.data;
}

export async function deleteEnrollmentPhoto(employeeId: number): Promise<EnrollmentPhotoDeleteResponse> {
  const response = await client.delete<EnrollmentPhotoDeleteResponse>(
    `admin/employees/${employeeId}/enrollmentPhoto`
  );
  return response.data;
}

export async function replaceEnrollmentPhoto(
  employeeId: number,
  file: File
): Promise<EnrollmentPhotoReplaceResponse> {
  const formData = new FormData();
  formData.append("photo", file);
  const response = await client.put<EnrollmentPhotoReplaceResponse>(
    `admin/employees/${employeeId}/enrollmentPhoto`,
    formData
  );
  return response.data;
}

/**
 * Re-queue enrollment of the photo already on file (TAS-568). 409 when there is
 * no photo or a job is already in flight — see `getEnrollmentRetryConflict`.
 */
export async function retryEnrollment(employeeId: number): Promise<EnrollmentRetryResponse> {
  const response = await client.post<EnrollmentRetryResponse>(
    `admin/employees/${employeeId}/enrollmentPhoto/retry`
  );
  return response.data;
}

/**
 * A 409 from retry carries the employee's current enrollment so the caller can
 * patch its row into the truth instead of only showing an error. Null for any
 * other failure.
 */
export function getEnrollmentRetryConflict(err: unknown): EnrollmentRetryConflict | null {
  if (!err || typeof err !== "object" || !("response" in err)) return null;
  const response = (err as { response?: { status?: number; data?: EnrollmentRetryConflict } }).response;
  if (response?.status !== 409 || !response.data || typeof response.data !== "object") return null;
  return response.data;
}

/** Everyone active whose enrollment failed or is still verifying (Selfie Exceptions → Enrollment). */
export async function getEnrollmentIssues(): Promise<EnrollmentIssuesResponse> {
  const response = await client.get<EnrollmentIssuesResponse>(`admin/selfieEnrollment/issues`);
  return response.data;
}

/**
 * Fallback when an older API build returns no `enrollment` on PUT: the job was
 * just queued. `previousEnrolledAt` is left null — only the API knows whether
 * the old face survives a replace.
 */
export function justQueuedEnrollment(): EmployeeEnrollment {
  const now = new Date().toISOString();
  return { state: "verifying", attempts: 1, queuedAt: now, enrolledAt: now };
}
